import { initContract } from "@ts-rest/core";
import { z } from "zod";
/**
 * Sensors (ADR-035) — the legacy "Sensoren verwalten" screen, which is READ + DELETE and nothing else: devices
 * register THEMSELVES, so there is no create and no edit anywhere in the legacy (its API surface was exactly
 * `GET /sensor`, `GET /sensor/{id}`, `DELETE /sensor/{id}`). SuperAdmin-only, as the legacy sidebar gated it.
 *
 * Two fields are renamed from the legacy on the way out (ADR-035 D5), because the legacy names describe the wrong
 * thing: `status` → `group` (it holds a project namespace — `PFSTraining`, `create.iot` — which the legacy screen
 * printed as "Network:") and the raw `settings` blob → a parsed `labels[]` (the legacy stored `{"lables":[…]}`, their
 * typo; it is parsed server-side so the typo never reaches the wire).
 */
const c = initContract();
const UnauthorizedSchema = z.object({ message: z.string() });
/** Not a SuperAdmin — the legacy gated the whole sidebar entry on exactly this. */
const SensorsForbiddenSchema = z.object({ message: z.string(), code: z.literal("SENSORS_FORBIDDEN") });
const SensorNotFoundSchema = z.object({ message: z.string(), code: z.literal("SENSOR_NOT_FOUND") });
/**
 * The six device types production actually holds. A device type the catalogue does not know yet must NOT break the
 * read (ADR-035 §6 #4), so this is a documented enum the WIRE does not enforce — `type` ships as a plain string and
 * the web treats anything outside the list as a plain sensor.
 */
export const KNOWN_SENSOR_TYPES = [
    "SwitchSensor",
    "MultiSwitchSensor",
    "HandSpeedSensor",
    "RemoteControlledSensor",
    "PassivePlateSensor",
    "SwitchBoxSensor",
];
/** The legacy's two tabs split on exactly this: a hand-speed device vs everything else. */
export const HAND_SPEED_SENSOR_TYPE = "HandSpeedSensor";
export const SensorSchema = z.object({
    id: z.number().int(),
    /** The technical device name, e.g. `T21_SwitchSensor_v7` — the legacy's fallback label. */
    name: z.string(),
    /** The human label, e.g. "Ablage: Plattendeckel". Never null in production, nullable in the schema. */
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    /** Free string, not an enum — see {@link KNOWN_SENSOR_TYPES}. */
    type: z.string().nullable(),
    /** The legacy `status` column: a project/owner namespace (`PFSTraining`, `create.iot`), never a status. */
    group: z.string().nullable(),
    /** The short device code the legacy shows in bold and sorts on (`T21`, `X03`). Unique per realm in production. */
    slug: z.string(),
    /** The device's address on the plant network. */
    ip: z.string(),
    /** Parsed from the legacy `{"lables":[…]}` blob — the per-switch labels a MultiSwitchSensor shows. `[]` when the
     *  blob is absent, empty or malformed (never a 500). */
    labels: z.array(z.string()),
    createdAt: z.string(), // ISO-8601 UTC
    /** The last time the row changed — the legacy printed this as "last login", which it is not. */
    updatedAt: z.string(),
});
/** How a manual referencing the sensor stands right now — the 409 body names these so the operator knows where to go. */
export const SensorManualStateSchema = z.enum(["live", "archived", "trashed"]);
export const SensorManualSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    facilityId: z.number().int(),
    facilityName: z.string(),
    /**
     * The manual's facility is archived. A manual inside an archived facility keeps `archived_at IS NULL` — archiving a
     * facility touches only `facility`, never the manuals under it — so it still reads `live` while being unreachable:
     * the navigator browses the active tree, so a deep link to it is dropped as stale and lands the caller on the root
     * list (diff panel). The detail panel uses this to keep such a row un-navigable rather than offering a dead end.
     */
    facilityArchived: z.boolean(),
    state: SensorManualStateSchema,
    /** The sensor is bound to the manual itself (the legacy `manual_sensors`, i.e. the handspeed picker). */
    viaManualLink: z.boolean(),
    /** The sensor is bound to a STEP element of this manual (`ar_element_setting.sensor_id`). */
    viaElement: z.boolean(),
});
export const SensorUsageSchema = z.object({
    manuals: z.array(SensorManualSchema),
    /** How many step-element settings bind this sensor (across every manual above). */
    elementSettings: z.number().int().nonnegative(),
});
export const SensorDetailSchema = z.object({ sensor: SensorSchema, usage: SensorUsageSchema });
/** 409: the sensor is still referenced — by a manual (live, archived OR trashed alike, ADR-035 D3) or by a
 *  blueprint element that belongs to no manual. */
export const SensorInUseSchema = z.object({
    message: z.string(),
    code: z.literal("SENSOR_IN_USE"),
    manuals: z.array(SensorManualSchema),
    /** Element bindings that block the delete — including BLUEPRINT elements, which belong to no manual and therefore
     *  never appear in `manuals` (diff panel). `manuals` empty + this > 0 means "bound to a blueprint element". */
    elementSettings: z.number().int().nonnegative(),
});
export const sensorContract = c.router({
    listSensors: {
        method: "GET",
        path: "/sensors",
        responses: { 200: z.object({ sensors: z.array(SensorSchema) }), 401: UnauthorizedSchema, 403: SensorsForbiddenSchema },
        summary: "Every sensor registered in the realm (SuperAdmin only) — the legacy Manage sensors list.",
    },
    getSensor: {
        method: "GET",
        path: "/sensors/:id",
        pathParams: z.object({ id: z.coerce.number().int().positive().max(2147483647) }),
        responses: { 200: SensorDetailSchema, 401: UnauthorizedSchema, 403: SensorsForbiddenSchema, 404: SensorNotFoundSchema },
        summary: "One sensor + which manuals use it (the detail panel; SuperAdmin only).",
    },
    deleteSensor: {
        method: "DELETE",
        path: "/sensors/:id",
        pathParams: z.object({ id: z.coerce.number().int().positive().max(2147483647) }),
        body: z.object({}).optional(),
        responses: {
            200: z.object({ deleted: z.literal(true) }),
            401: UnauthorizedSchema,
            403: SensorsForbiddenSchema,
            404: SensorNotFoundSchema,
            409: SensorInUseSchema,
        },
        summary: "Soft-delete a sensor (SuperAdmin only). 409 while ANY manual references it — live, archived or trashed (ADR-035 D3).",
    },
});
//# sourceMappingURL=sensor.contract.js.map